import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { usePreviewSchedule } from "routes/schedule/preview-schedule"
import { Box, Checkbox, Chip, FormControlLabel, IconButton, ListItem, Stack } from "@mui/material"
import { ListItemBox } from "lib/@components/ListItemBox"
import { WheelChart } from "lib/@components/wheel-chart"
import { setChosenSchedule } from "routes/asset-register/plugins/asset-mapping/controller/set-chosen-schedule"
import { recordSelection } from "routes/asset-register/plugins/asset-mapping/plugins/ai-mapping-feedback/record-selection"
import { MappingUpdated } from "routes/asset-register/plugins/asset-mapping/asset-mapping.runtime"
import { useTreeItem } from "routes/schedule/lib/useTreeItem"
import { alert } from "lib/@dialogs/alert"
import { parentFind, parentTreeItem } from "library/tree/index.mjs"
import { useMemo } from "react"
import { AssetAvailableSchedule, AssetAvailableScheduleItems } from "slot-definitions"
import { Bound } from "lib/@components/binding/Bound"
import { Repeat } from "lib/@components/repeat"
import { ScheduleLabel } from "routes/asset-register/plugins/asset-mapping/schedule-label"
import Iconify from "minimals-template/components/Iconify"
import { SORT_FROM_CLASSIFICATION } from "library/SFG20/constants.mjs"
import { AssetMappingCriticalityIcons } from "routes/asset-register/plugins/asset-mapping/asset-mapping-criticality-icons.js"

export function AvailableSchedule({ item }) {
    const original = item
    const { assetType, register, isManualMapTab } = useBoundContext()
    item = assetType.schedules?.find((s) => s.schedule_id === item.schedule_id) ?? item
    useMemo(() => {
        if (!item) return
        Object.assign(item, original)
    }, [item, original])
    const preview = usePreviewSchedule(item?.schedule_id, {
        fullScreen: false,
        boost: false,
        sx: {
            my: 2,
            height: 1,
            flex: 1,
            alignSelf: "stretch",
            justifyContent: "stretch",
        },
    })
    const treeItem = useTreeItem(item?.schedule_id + ":sfg20/schedule")
    const specialistParent = parentFind(treeItem, (s) => s[parentTreeItem]?.label === " SFG20")
    const previouslyMappedSchedule = item.previously_mapped

    const listOfCriticalities = Object.entries(treeItem?.$?.taskTypes ?? {})
        .filter(([, count]) => count > 0)
        .sortBy(([type]) => SORT_FROM_CLASSIFICATION[type])
    const disableSelection = isManualMapTab

    return (
        !!treeItem && (
            <ListItem sx={{ py: 0 }}>
                <Stack sx={{ width: 1 }}>
                    <AssetAvailableScheduleItems.Slot item={item} scheduleId={item.schedule_id} treeItem={treeItem}>
                        <ListItemBox data-cy="suggested-schedule">
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        data-cy={`suggested-schedule-checkbox-${item.schedule_id}`}
                                        data-chosen={!!item.chosen}
                                        onClick={handleSelectSchedule}
                                        disabled={disableSelection}
                                        checked={!!item.chosen}
                                    />
                                }
                                label={
                                    <Stack
                                        direction="row"
                                        sx={{ color: treeItem.notLicensed ? "secondary.dark" : undefined }}
                                        spacing={1}
                                        alignItems="center"
                                    >
                                        <WheelChart
                                            size={20}
                                            color={+item.relevance >= 101 ? "info.main" : "success.main"}
                                            label={+item.relevance}
                                            percentage={Math.min(100, +item.relevance)}
                                        />
                                        <Box>
                                            <ScheduleLabel label={treeItem.label} code={treeItem.code} />{" "}
                                            {treeItem.notLicensed && `[${specialistParent.label}]`}
                                        </Box>
                                        {!!listOfCriticalities.length && (
                                            <Chip
                                                size="small"
                                                label={
                                                    <Stack direction="row" spacing={0.6}>
                                                        <Repeat
                                                            list={listOfCriticalities}
                                                            item={<AssetMappingCriticalityIcons />}
                                                        />
                                                    </Stack>
                                                }
                                            />
                                        )}
                                    </Stack>
                                }
                            />
                            <Bound schedule={{ _id: item.schedule_id, label: treeItem.label }}>
                                <AssetAvailableSchedule.Slot>
                                    {!treeItem.notLicensed && (
                                        <IconButton
                                            aria-label="Preview schedule"
                                            onClick={preview}
                                            color="primary"
                                            size="small"
                                            data-cy={`suggested-schedule-preview-${item.schedule_id}`}
                                        >
                                            <Iconify icon="mdi:eye" />
                                        </IconButton>
                                    )}
                                </AssetAvailableSchedule.Slot>
                            </Bound>
                            {previouslyMappedSchedule && (
                                <Box sx={{ pl: 1, ml: "auto" }}>
                                    <Chip
                                        sx={{ px: 0.5 }}
                                        icon={<Iconify icon="mdi:warning" />}
                                        size="small"
                                        color={item.chosen ? "primary" : "warning"}
                                        label="Previously Mapped"
                                    />
                                </Box>
                            )}
                            {/* <Box flex={1} /> */}
                        </ListItemBox>
                    </AssetAvailableScheduleItems.Slot>
                </Stack>
            </ListItem>
        )
    )

    function notLicensed() {
        return alert(
            `You have not licensed that schedule, which is part of the "${specialistParent.label}" specialist set.`,
            "Unlicensed"
        )
    }

    function change() {
        assetType.doNotMap = false
        if (item.chosen) {
            item.chosen = false
            original.chosen = item.chosen
            setChosenSchedule(register.id, assetType.name, `-${item.schedule_id}`).catch(console.error)
            MappingUpdated.raiseOnce()
        } else {
            item.chosen = true
            original.chosen = item.chosen
            setChosenSchedule(register.id, assetType.name, `+${item.schedule_id}`).catch(console.error)
            MappingUpdated.raiseOnce()
        }
        recordSelection({ register, entry: assetType })
    }

    function handleSelectSchedule() {
        if (disableSelection) return undefined
        if (treeItem?.notLicensed) return notLicensed()
        return change()
    }
}
